/**
 * Zod schemas for API responses
 * Eliminates "unknown" type issues and provides runtime validation
 */

import { z } from "zod";

/**
 * Google Places Autocomplete API
 */
export const PlacesAutocompleteSchema = z.object({
  status: z.string(),
  predictions: z.array(
    z.object({
      description: z.string(),
      place_id: z.string(),
      structured_formatting: z.any().optional(),
      terms: z.array(z.any()).optional(),
      types: z.array(z.string()).optional(),
    })
  ),
  error_message: z.string().optional(),
});
export type PlacesAutocomplete = z.infer<typeof PlacesAutocompleteSchema>;

/**
 * Google Places Details API
 */
export const PlacesDetailsSchema = z.object({
  status: z.string(),
  result: z
    .object({
      place_id: z.string(),
      name: z.string().optional(),
      formatted_address: z.string().optional(),
      geometry: z
        .object({
          location: z.object({
            lat: z.number(),
            lng: z.number(),
          }),
        })
        .optional(),
      address_components: z.array(z.any()).optional(),
    })
    .optional(),
  error_message: z.string().optional(),
});
export type PlacesDetails = z.infer<typeof PlacesDetailsSchema>;

/**
 * Kakao OAuth Token Response
 */
export const KakaoAuthTokenSchema = z.object({
  customToken: z.string(),
  user: z
    .object({
      uid: z.string(),
      email: z.string().optional(),
      displayName: z.string().optional(),
      photoURL: z.string().optional(),
    })
    .optional(),
});
export type KakaoAuthToken = z.infer<typeof KakaoAuthTokenSchema>;

/**
 * Ad Serving API Response
 */
export const AdsServeSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      advertiser_name: z.string(),
      target_url: z.string().url(),
      image_url: z.string().url().optional(),
      placement: z.string().optional(),
      priority: z.number().optional(),
    })
  ),
  stats: z
    .object({
      total: z.number(),
      active: z.number().optional(),
    })
    .optional(),
});
export type AdsServe = z.infer<typeof AdsServeSchema>;

/**
 * Admin Billing Dashboard API
 */
export const BillingDashboardSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      status: z.string(),
      amount: z.number(),
      currency: z.string(),
      created: z.number(),
      customer_email: z.string().optional(),
    })
  ),
  stats: z
    .object({
      total: z.number(),
      revenue: z.number().optional(),
    })
    .optional(),
});
export type BillingDashboard = z.infer<typeof BillingDashboardSchema>;

/**
 * Admin Stats API
 */
export const AdminStatsSchema = z.object({
  total_impressions: z.number(),
  total_clicks: z.number(),
  total_revenue: z.number(),
  ctr: z.number().optional(),
  by_date: z
    .array(
      z.object({
        date: z.string(),
        impressions: z.number(),
        clicks: z.number(),
        revenue: z.number(),
      })
    )
    .optional(),
});
export type AdminStats = z.infer<typeof AdminStatsSchema>;

/**
 * Generic API Response
 */
export const ApiResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string().optional(),
  data: z.any().optional(),
  error: z.string().optional(),
});
export type ApiResponse = z.infer<typeof ApiResponseSchema>;

/**
 * Region/City Autocomplete
 */
export const RegionAutocompleteSchema = z.object({
  suggestions: z.array(
    z.object({
      state: z.string(),
      city: z.string().optional(),
      suburb: z.string().optional(),
      formatted: z.string(),
    })
  ),
});
export type RegionAutocomplete = z.infer<typeof RegionAutocompleteSchema>;

/**
 * Like/Unlike Response
 */
export const LikeResponseSchema = z.object({
  ok: z.boolean(),
  liked: z.boolean(),
  count: z.number(),
});
export type LikeResponse = z.infer<typeof LikeResponseSchema>;

/**
 * Comment Response
 */
export const CommentResponseSchema = z.object({
  ok: z.boolean(),
  comment: z
    .object({
      id: z.string(),
      content: z.string(),
      userId: z.string(),
      userName: z.string(),
      createdAt: z.string(),
    })
    .optional(),
});
export type CommentResponse = z.infer<typeof CommentResponseSchema>;

/**
 * Upload Response
 */
export const UploadResponseSchema = z.object({
  ok: z.boolean(),
  url: z.string().optional(),
  key: z.string().optional(),
  error: z.string().optional(),
});
export type UploadResponse = z.infer<typeof UploadResponseSchema>;

/**
 * YouTube Video Info
 */
export const YouTubeVideoSchema = z.object({
  id: z.string(),
  title: z.string(),
  titleKo: z.string().optional(),
  description: z.string().optional(),
  descriptionKo: z.string().optional(),
  thumbnailUrl: z.string().url(),
  channelId: z.string(),
  channelTitle: z.string(),
  publishedAt: z.string(),
  duration: z.string().optional(),
  viewCount: z.number().optional(),
  isShorts: z.boolean().optional(),
});
export type YouTubeVideo = z.infer<typeof YouTubeVideoSchema>;

/**
 * Single Advertisement (for AdBanner)
 */
export const AdvertisementSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    advertiser_name: z.string().optional(),
    advertiserName: z.string().optional(),
    business_name: z.string().optional(),
    businessName: z.string().optional(),
    target_url: z.string().url().optional(),
    targetUrl: z.string().url().optional(),
    link: z.string().url().optional(),
    image_url: z.string().url().optional(),
    imageUrl: z.string().url().optional(),
    mobileImageUrl: z.string().url().optional(),
    placement: z.string().optional(),
    priority: z.number().optional(),
    region: z.string().optional(),
    cta_text: z.string().optional(),
    ctaText: z.string().optional(),
  })
  .transform((data) => ({
    id: data.id,
    title: data.title,
    advertiser_name:
      data.advertiser_name ||
      data.advertiserName ||
      data.business_name ||
      data.businessName ||
      "광고주",
    target_url: data.target_url || data.targetUrl || data.link,
    link: data.link || data.target_url || data.targetUrl,
    image_url: data.image_url || data.imageUrl,
    imageUrl: data.imageUrl || data.image_url,
    mobileImageUrl: data.mobileImageUrl,
    placement: data.placement,
    priority: data.priority,
    region: data.region,
    cta_text: data.cta_text || data.ctaText,
  }));
export type AdvertisementData = z.infer<typeof AdvertisementSchema>;

/**
 * Billing Subscriptions List
 */
export const BillingSubscriptionsSchema = z.object({
  items: z.array(
    z.object({
      id: z.number(),
      advertiser_id: z.number(),
      stripe_customer_id: z.string(),
      stripe_subscription_id: z.string(),
      plan_id: z.string(),
      status: z.string(),
      current_period_end: z.string(),
      created_at: z.string(),
      business_name: z.string(),
      contact_email: z.string(),
      region: z.string(),
      ads_count: z.number(),
      active_ads_count: z.number(),
    })
  ),
  stats: z
    .object({
      total: z.number(),
      active: z.number(),
      canceled: z.number(),
      past_due: z.number(),
    })
    .optional(),
});
export type BillingSubscriptions = z.infer<typeof BillingSubscriptionsSchema>;

/**
 * Billing Invoices List
 */
export const BillingInvoicesSchema = z.object({
  items: z.array(
    z.object({
      id: z.number(),
      subscription_id: z.number(),
      stripe_invoice_id: z.string(),
      amount_cents: z.number(),
      currency: z.string(),
      status: z.string(),
      period_start: z.string(),
      period_end: z.string(),
      created_at: z.string(),
      plan_id: z.string(),
      business_name: z.string(),
      contact_email: z.string(),
    })
  ),
  total: z
    .object({
      total_count: z.number(),
      total_amount: z.number(),
    })
    .optional(),
});
export type BillingInvoices = z.infer<typeof BillingInvoicesSchema>;

/**
 * Billing Events List
 */
export const BillingEventsSchema = z.object({
  items: z.array(
    z.object({
      id: z.number(),
      stripe_event_id: z.string(),
      type: z.string(),
      status: z.string(),
      payload_hash: z.string(),
      error_message: z.string().nullable(),
      processed_at: z.string(),
    })
  ),
  stats: z
    .object({
      total: z.number(),
      success: z.number(),
      failure: z.number(),
      processing: z.number(),
    })
    .optional(),
});
export type BillingEvents = z.infer<typeof BillingEventsSchema>;
